import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  PlayCircleOutline,
  Description,
  Assignment,
  Quiz,
  MenuBook,
  AccountTree,
  ExpandMore,
  ArrowForward,
  CheckCircle,
  Lock,
  Info,
  Warning,
  CircleOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useContentPlayer } from '../../context/ContentPlayerContext';
import { useUser } from '../../context/UserContext';
import { useToast } from '../../context/ToastContext';
import { learningPathService } from '../../services/api';
import TypeTag from '../shared/TypeTag';
import EmptyState from '../common/EmptyState';

const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const ModuleItem = styled(ListItem)(({ theme, completed, isLast, isEnrolledInCurrentInstance }) => ({
  marginBottom: theme.spacing(1),
  //backgroundColor: completed && isEnrolledInCurrentInstance ? 'rgba(76, 175, 80, 0.08)' : 'transparent',
  '&:hover': {
    backgroundColor: completed ? 'rgba(76, 175, 80, 0.12)' : 'rgba(0, 0, 0, 0.04)',
    //backgroundColor:'rgba(0, 0, 0, 0.04)',
    //boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
  },
  //border: '1px solid #bbb',
  borderRadius: '8px',
  'margin-bottom': '10px',
  cursor: 'pointer'
}));

const LearningPathModules = ({ contents, lpathId, enrolled = true, lpath = {}, refreshOnClose, firstIncompleteContent, enrolledObj, instance }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { openContentPlayer } = useContentPlayer();
  const { showToast } = useToast();
  const isAdminOrAuthor = user && [1, 11, 5, 7].includes(user.current_role_id);

  const [imageErrors, setImageErrors] = useState({});
  const [selectedInstances, setSelectedInstances] = useState({});
  // Determine if all modules are required or just some
  const totalContents = contents?.length || 0;
  const requiredCount = lpath.required_count || 0;
  const allModulesRequired = requiredCount === totalContents && requiredCount > 0;
  const someModulesRequired = requiredCount > 0 && requiredCount < totalContents;

  const isLearnerMode = !isAdminOrAuthor;
  const isEnrolledInCurrentInstance = enrolledObj && instance && enrolledObj.lpath_instance_id === instance.id;
  const isFlexibleInstance = instance && instance.flexible === '1';

  const [lockedContent, setLockedContent] = useState({});
  const [contentLockStatus, setContentLockStatus] = useState(null);

  const handleImageError = (contentId) => {
    setImageErrors(prev => ({
      ...prev,
      [contentId]: true
    }));
  };

  // Add this function to handle content click
  const handleContentClick = (content, isLocked) => {
    if (isLocked) {
      showToast("Please complete the previous content first", "warning");
      return;
    }

    if (!enrolled && !isAdminOrAuthor) {
      showToast("Please enroll to access this content", "warning");
      return;
    }

    if (content.type === 'course') {
      navigate(`/learner/course/${content.id}`);
    } else if (content.type === 'learning path') {
      navigate(`/learner/lpath/${content.id}`);
    }
  };

  const handleActivityClick = (courseId, activity, isLocked) => {
    if (isLocked) {
      showToast("Please complete the previous content first", "warning");
      return;
    }
    
    if (isAdminOrAuthor || enrolled) {
      openContentPlayer(
        courseId,
        activity.ref_id,
        `/learner/lpath/${lpathId}`,
        () => {
          // This callback will be called when the content player is closed
          if (refreshOnClose) refreshOnClose();
        },
        isLearnerMode,
        activity.clr_id || null,
        lpathId,
        () => {
          // This callback will be called when the content player is closed
          if (refreshOnClose) refreshOnClose();
        }
      );
    }
    else {
      showToast("Enrollment required to access content", "warning");
      return;
    }
  };

  // Add a function to handle instance selection
  const handleInstanceChange = (content, instanceId) => {
    // Extract content ID and type
    const contentId = content.id;
    const contentType = content.type;

    setSelectedInstances(prev => ({
      ...prev,
      [`${contentType}-${contentId}`]: instanceId
    }));

    // Make API call to update the instance
    learningPathService.updateCourseInstance(lpathId, contentId, contentType, instanceId)
      .then(response => {
        if (response.data.success) {
          showToast("Instance updated successfully", "success");

          if (content.selectedInstanceId !== undefined) {
            content.selectedInstanceId = instanceId;
          }

          // Refresh data if callback provided
          //if (refreshOnClose) refreshOnClose();
        } else {
          showToast(response.data.message || "Failed to update instance", "error");

          // Revert the local state change on error
          setSelectedInstances(prev => ({
            ...prev,
            [`${contentType}-${contentId}`]: content.selectedInstanceId || ''
          }));

        }
      })
      .catch(error => {
        console.error("Error updating instance:", error);
        showToast(error.response?.data?.message || "Failed to update instance", "error");

        // Revert the local state change on error
        setSelectedInstances(prev => ({
          ...prev,
          [`${contentType}-${contentId}`]: content.selectedInstanceId || ''
        }));
      });
  };

  const getContentIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'video':
        return <PlayCircleOutline />;
      case 'document':
        return <Description />;
      // case 'assignment':
      //   return <Assignment />;
      // case 'quiz':
      //   return <Quiz />;
      case 'course':
        return <MenuBook color="primary" />;
      case 'learning path':
        return <AccountTree color="success" />;
      default:
        return <Description />;
    }
  };

  const getContentTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'course':
        return '#1976d2';
      case 'learning path':
        return '#014361';
      default:
        return '#757575';
    }
  };

  useEffect(() => {
    if (contents && contents.length > 0) {
      const initialSelections = {};
      // Helper function to process content items recursively
      const processContent = (items) => {
        items.forEach(content => {
          // If this content has a selected instance, add it to our state
          if (content.selectedInstanceId || content.instance_id) {
            initialSelections[`${content.type}-${content.id}`] =
              content.selectedInstanceId || content.instance_id;
          }

          // Process nested content if it exists
          if (content.children && content.children.length > 0) {
            processContent(content.children);
          }
        });
      };
      processContent(contents);
      setSelectedInstances(initialSelections);
    }
  }, [contents]);

  // Fetch lock status when data changes
  useEffect(() => {
    if (enrolledObj && lpath?.id) {
      console.log("Learner ID:", enrolledObj.learner_id);
      const fetchLockStatus = async () => {
        try {
          const response = await learningPathService.getContentLockStatus(
            lpath.id,
            user?.id
          );    
                
          if (response.data.success) {
            const locked = {};
            response.data.data.locked_items.forEach(item => {
              locked[item.key] = true;
            });            
            setLockedContent(locked);
            setContentLockStatus(response.data.data);
          }
        } catch (error) {
          console.error('Error fetching lock status:', error);
        }
      };
      fetchLockStatus();
    }
  }, [isLearnerMode, enrolledObj, lpath?.id, user?.id]);

  // Function to render course modules and units exactly like CourseModules
  const renderCourseContents = (courseData) => {
    // Check for different possible data structures
    const activities = courseData?.contents || [];

    if (!activities || activities.length === 0) {
      // The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14, PR #8 review):
      // a plain line.
      return <EmptyState framed={false} title="No content available for this course." message={null} />;
    }

    return (
      <List>
        {activities.map((activity, index) => {
          const isLast = index === activities.length - 1;
          const isCompleted = courseData.progress &&
            courseData.progress.completed_items &&
            courseData.progress.completed_items.includes(parseInt(activity.ref_id));

          return (
            <ModuleItem
              key={activity.ref_id || index}
              button
              onClick={() => handleActivityClick(courseData.id, activity)}
              completed={isCompleted ? 1 : 0}
              isEnrolledInCurrentInstance={isEnrolledInCurrentInstance}
              isLast={isLast}
              sx={{ py: 1.5 }}
            >
              <ListItemIcon>
                {/*getContentIcon(activity.type)*/}
                <TypeTag type="module" compact />
              </ListItemIcon>
              <ListItemText sx={{ml: 1}}
                primary={activity.name || activity.title}
                //secondary={`${(activity.type || 'Content').charAt(0).toUpperCase() + (activity.type || 'Content').slice(1)}`}
                secondary={/* '1 min', not '1 mins' (N44 pt 11) */ `${activity.duration ? `${activity.duration} ${Number(activity.duration) === 1 ? 'min' : 'mins'}` : ''}`}
              />
              <ListItemSecondaryAction>
                {isEnrolledInCurrentInstance && isCompleted ? (
                  <Tooltip title="Completed">
                    <CheckCircle color="success" />
                  </Tooltip>
                ) : !enrolled ? (
                  <Tooltip title="Enroll to access">
                    <Lock color="action" />
                  </Tooltip>
                ) : (
                  <Tooltip title="Start">
                    <CircleOutlined />
                    {/*
                    <IconButton edge="end" onClick={(e) => {
                      e.stopPropagation();
                      handleActivityClick(courseData.id, activity);
                    }}>
                      <ArrowForward />
                    </IconButton>
                    */}
                  </Tooltip>
                )}
              </ListItemSecondaryAction>
            </ModuleItem>
          );
        })}
      </List>
    );
  };

  // Helper function to check if content is locked
  const isContentLocked = (contentItem, index, contentItems) => {

    // If learning path is not ordered, nothing is locked
    if (lpath?.content_ordered === '0' || lpath?.content_ordered === 0 || lpath?.content_ordered === false) {
      return false;
    }

    // Check if this specific content is in locked list
    const contentKey = `${contentItem.id}|${contentItem.type}`;
    return lockedContent[contentKey] === true;
  };
  // Recursive function to render content at any nesting level
  const renderContent = (contentItems, level = 0) => {
    if (!contentItems || contentItems.length === 0) {
      // The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14, PR #8 review):
      // a plain line.
      return <EmptyState title="No content available." message={null} />;
    }

    return (
      <Box className={`learning-path-contents level-${level}`}>
        {contentItems.map((content, index) => {
          const contentId = `${content.type}-${content.id}-${level}-${index}`;
          const coverImage = content.cover_picture
            ? `/uploads/${content.type === 'course' ? 'courses' : 'learning_paths'}/${content.cover_picture}`
            : `/assets/images/course-default.jpg`;
          const hasImageError = imageErrors[contentId];
          const typeColor = getContentTypeColor(content.type);

          // Determine if this specific content is required
          const isRequired = content.required === "1";

          // Check if content is completed
          const isCompleted = content.progress && (content.progress.status === '1' || (content.progress.percentage === 100));

          // Calculate progress percentage correctly - ensure it doesn't exceed 100%
          const progressPercentage = content.progress && content.progress.percentage
            ? Math.min(content.progress.percentage, 100)
            : (content.progress && content.progress.total > 0
              ? Math.min(Math.round((content.progress.completed / content.progress.total) * 100), 100)
              : 0);

          // Determine if content is in progress
          const isInProgress = !isCompleted && content.progress &&
            (content.progress.status === '2' ||
              (content.progress.percentage > 0 && content.progress.percentage < 100));

          // Check if this content has available instances
          const hasAvailableInstances = isFlexibleInstance &&
            content.available_instances &&
            content.available_instances.length > 1;

          const isLocked = isContentLocked(content, index, contentItems);

          return (
            <Accordion
              key={`accordion-${contentId}`}
              defaultExpanded={level === 0 && index === 0}
              disabled={isLocked}
              //className={`content-accordion ${level > 0 ? 'nested-accordion' : ''} ${isEnrolledInCurrentInstance && isCompleted ? 'completed-accordion' : ''}`}
              className={`content-accordion ${level > 0 ? 'nested-accordion' : ''} ${isCompleted ? 'completed-accordion' : ''}`}
              sx={{
                mb: 2,
                //...(isCompleted && { backgroundColor: 'rgba(76, 175, 80, 0.08)' }),
                ...(isRequired && { border: '1px solid #fb8c00' }),
                ...(isLocked && { 
                  opacity: 0.5,
                  backgroundColor: 'rgba(0, 0, 0, 0.03)',
                  pointerEvents: 'none'
                })
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={`panel-${content.id}-${level}-content`}
                id={`panel-${content.id}-${level}-header`}
                className="content-accordion-summary"
                sx={{
                  minHeight: '80px',
                  position: 'relative',
                  ...(isLocked && {
                    backgroundColor:'rgba(200, 200, 200, 0.1)',
                    cursor: 'not-allowed !important'
                  }),
                  // '&:hover': {
                  //   backgroundColor: isEnrolledInCurrentInstance && isCompleted ? 'rgba(76, 175, 80, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                  // },
                  '&:hover': {
                    backgroundColor: isLocked 
                      ? 'rgba(200, 200, 200, 0.15)' 
                      : (isCompleted ? 'rgba(76, 175, 80, 0.12)' : 'rgba(0, 0, 0, 0.04)'),
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <TypeTag type={content.type} compact />

                  <Box sx={{ flexGrow: 1, ml:1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Typography sx={{ fontWeight: 500, mr: 1 }} className="content-title">
                        {content.name}
                      </Typography>

                      {/* Add lock indicator for locked content */}
                      {isLocked && (
                        <Tooltip title="Complete previous content to unlock">
                          <Lock fontSize="small" sx={{ mr: 1, color: '#999' }} />
                        </Tooltip>
                      )}

                      {/* Show Required chip if this content is required */}
                      {isRequired && level === 0 && (
                        <Chip
                          icon={<Warning fontSize="small" />}
                          label="Required"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ height: 24, mr: 1 }}
                        />
                      )}

                      {/* Show Completed chip if completed */}
                      {isCompleted && (<CheckCircle fontSize="small" />)}
                      {/*
                      {isEnrolledInCurrentInstance && isCompleted && (
                        <Chip
                          icon={<CheckCircle fontSize="small" />}
                          label="Completed"
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ height: 24 }}
                        />
                      )}
                      */}
                      {/* Show progress chip if in progress */}
                      {isEnrolledInCurrentInstance && isInProgress && (
                        <Chip
                          icon={<Info fontSize="small" />}
                          label="In Progress"
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ height: 24 }}
                        />
                      )}
                    </Box>
                      {/*}
                    <Chip
                      label={
                        content.type === 'learning path'
                          ? 'LP'
                          : content.type === 'course'
                            ? 'C'
                            : (content.type || 'Content').charAt(0).toUpperCase() + (content.type || 'Content').slice(1)
                      }
                      size="small"
                      sx={{
                        mt: 0.5,
                        backgroundColor:
                          content.type === 'course'
                            ? 'rgba(25, 118, 210, 0.10)'
                            : content.type === 'learning path'
                              ? 'rgba(1, 67, 97, 0.10)'
                              : 'rgba(117, 117, 117, 0.10)',
                        color:
                          content.type === 'course'
                            ? '#1976d2'
                            : content.type === 'learning path'
                              ? '#014361'
                              : '#757575',
                        fontWeight: 600,
                        ...(isLocked && { opacity: 0.7 }),
                      }}
                    />
                    */}
                  </Box>

                  {/* Show locked icon in secondary action for locked content */}
                  {isLocked &&  (
                    <Box sx={{ minWidth: 50, textAlign: 'center' }}>
                      <Tooltip title="Locked - Complete prerequisites">
                        <Lock sx={{ color: '#ccc' }} />
                      </Tooltip>
                    </Box>
                  )}

                  {/* Add instance selection dropdown for flexible instances */}
                  {hasAvailableInstances && isEnrolledInCurrentInstance && (
                    <Box sx={{ minWidth: 150, mr: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel id={`instance-select-${contentId}`}>Instance</InputLabel>
                        <Select
                          labelId={`instance-select-${contentId}`}
                          id={`instance-select-input-${contentId}`}
                          value={content.selectedInstanceId ? content.selectedInstanceId.toString() : ''}
                          onChange={(e) => handleInstanceChange(content, e.target.value)}
                          label="Instance"
                          onClick={(e) => e.stopPropagation()} // Prevent accordion from toggling
                        >
                          {content.available_instances.map((inst) => {
                            return (
                              <MenuItem key={inst.id} value={inst.id.toString()}>
                                {inst.title || `Instance ${inst.id}`}
                                {inst.default_instance === '1' && " (Default)"}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails className="content-accordion-details">
                {content.type === 'course' && renderCourseContents(content)}

                {content.type === 'learning path' && content.children && content.children.length > 0 && (
                  // Recursively render nested learning path contents
                  renderContent(content.children, level + 1)
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  return (
    <SectionPaper elevation={0}>      
      {/* Show flexible instance notice if applicable */}
      {isFlexibleInstance && isEnrolledInCurrentInstance && (
        <>
          <Typography variant="h5" component="h2" gutterBottom>
            Modules
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: '8px' }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
              <Info color="info" sx={{ mr: 1 }} />
              Flexible Instance
            </Typography>
            <Typography variant="body2">
              This is a flexible learning path instance. You can select different instances for each course.
            </Typography>
          </Box>
        </>
      )}

      <Typography variant="h5" component="h2" gutterBottom>
        Core Content
      </Typography>

      {contents && contents.length > 0 ? (
        renderContent(contents)
      ) : (
        <>
          {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14, PR #8 review): a plain line. */}
          <EmptyState framed={false} title="No content available for this learning path." message={null} />
        </>
      )}
    </SectionPaper>
  );
};

export default LearningPathModules;